import { useCallback, useEffect, useMemo, useState } from "react";
import { Activity, Cpu, Database, HardDrive, Loader2, MemoryStick, RefreshCw, Zap } from "lucide-react";
import { Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtNum, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { KpiCard, SectionCard, Segmented, InfoNote } from "@/components/common/Cards";

const GB = 1024 ** 3;
const MB = 1024 ** 2;

function fmtBytes(n) {
  if (!n && n !== 0) return "–";
  if (n >= GB) return `${(n / GB).toFixed(2)} GB`;
  if (n >= MB) return `${(n / MB).toFixed(1)} MB`;
  return `${(n / 1024).toFixed(0)} KB`;
}

function fmtUptime(sec) {
  if (!sec) return "–";
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (d) return `${d}h ${h}j ${m}m`;
  if (h) return `${h}j ${m}m`;
  return `${m}m`;
}

function ProgressRow({ label, percent, right, tone }) {
  const p = Math.max(0, Math.min(100, percent || 0));
  const color =
    tone === "danger" || p >= 85 ? "bg-[hsl(0_72%_55%)]" : tone === "warn" || p >= 70 ? "bg-[hsl(35_80%_55%)]" : "bg-primary";
  return (
    <div>
      <div className="flex items-center justify-between text-[12px] mb-1.5">
        <span className="text-muted-foreground">{label}</span>
        <span className="num text-foreground">{right}</span>
      </div>
      <div className="h-2 w-full rounded-full bg-[hsl(220_10%_12%)] overflow-hidden">
        <div className={`h-full ${color} transition-all duration-500`} style={{ width: `${p}%` }} />
      </div>
    </div>
  );
}

function ChartBlock({ points, hours }) {
  const data = useMemo(() => {
    if (!points?.length) return [];
    return points.map((p) => ({
      t: p.at,
      label: hours <= 24 ? new Date(p.at).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" }) : new Date(p.at).toLocaleDateString("id-ID", { day: "2-digit", month: "short" }),
      cpu: p.cpu,
      mem: p.mem_percent,
      disk: p.disk_percent,
    }));
  }, [points, hours]);

  if (!points?.length) {
    return (
      <div className="h-[240px] flex items-center justify-center text-xs text-muted-foreground" data-testid="res-chart-empty">
        Belum ada sampel historis. Server akan mengumpulkan data setiap 10 menit.
      </div>
    );
  }

  return (
    <div className="h-[260px] w-full" data-testid="res-chart">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="cpuG" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="hsl(43 80% 55%)" stopOpacity={0.5} />
              <stop offset="100%" stopColor="hsl(43 80% 55%)" stopOpacity={0.05} />
            </linearGradient>
            <linearGradient id="memG" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="hsl(200 70% 55%)" stopOpacity={0.5} />
              <stop offset="100%" stopColor="hsl(200 70% 55%)" stopOpacity={0.05} />
            </linearGradient>
            <linearGradient id="diskG" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="hsl(160 55% 45%)" stopOpacity={0.5} />
              <stop offset="100%" stopColor="hsl(160 55% 45%)" stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(220 10% 18%)" />
          <XAxis dataKey="label" tick={{ fill: "hsl(45 10% 60%)", fontSize: 11 }} stroke="hsl(220 10% 25%)" />
          <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fill: "hsl(45 10% 60%)", fontSize: 11 }} stroke="hsl(220 10% 25%)" />
          <Tooltip
            contentStyle={{ background: "hsl(220 14% 6%)", border: "1px solid hsl(220 10% 20%)", borderRadius: 8, fontSize: 12 }}
            formatter={(v) => `${Number(v || 0).toFixed(1)}%`}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Area type="monotone" dataKey="cpu" name="CPU" stroke="hsl(43 80% 55%)" fill="url(#cpuG)" strokeWidth={2} />
          <Area type="monotone" dataKey="mem" name="Memory" stroke="hsl(200 70% 55%)" fill="url(#memG)" strokeWidth={2} />
          <Area type="monotone" dataKey="disk" name="Disk /app" stroke="hsl(160 55% 45%)" fill="url(#diskG)" strokeWidth={2} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export function ResourcePanel() {
  const [snap, setSnap] = useState(null);
  const [points, setPoints] = useState(null);
  const [hours, setHours] = useState(24);
  const [usage, setUsage] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const [sRes, hRes, uRes] = await Promise.all([
        api.get("/admin/resources/snapshot"),
        api.get("/admin/resources/history", { params: { hours } }),
        api.get("/admin/usage"),
      ]);
      setSnap(sRes.data);
      setPoints(hRes.data.points || []);
      setUsage(uRes.data);
    } catch (e) {
      if (!silent) toast.error(errMsg(e, "Gagal memuat laporan resource"));
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, [hours]);

  useEffect(() => {
    loadAll();
    const t = setInterval(() => loadAll(true), 30000); // 30s auto refresh
    return () => clearInterval(t);
  }, [loadAll]);

  if (loading) {
    return (
      <div className="space-y-4" data-testid="res-loading">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}
        </div>
        <Skeleton className="h-[280px] rounded-xl" />
      </div>
    );
  }

  const cpu = snap?.cpu || {};
  const mem = snap?.memory || {};
  const disks = snap?.disks || [];
  const db = snap?.db || {};
  const month = usage?.month;
  const totalUsed = usage?.total_tokens || 0;
  const defaultLimit = usage?.default_limit || 100000;
  const totalReq = usage?.total_requests || 0;
  const users = usage?.users || [];
  const globalPct = defaultLimit > 0 ? Math.min(100, (totalUsed / (defaultLimit * Math.max(1, users.length))) * 100) : 0;

  return (
    <div className="space-y-5" data-testid="resource-panel">
      {/* Live KPIs */}
      <SectionCard
        eyebrow="Status Server"
        title="Resource server (live)"
        description={`Diperbarui otomatis tiap 30 detik. Terakhir: ${snap?.at ? fmtDate(snap.at) : "–"}. Uptime: ${fmtUptime(snap?.uptime_sec)}.`}
        testid="res-live-card"
        actions={
          <Button size="sm" variant="outline" onClick={() => loadAll()} disabled={refreshing} data-testid="res-refresh">
            {refreshing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
            Refresh
          </Button>
        }
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard
            label="CPU"
            value={`${(cpu.percent || 0).toFixed(1)}%`}
            icon={Cpu}
            tone={(cpu.percent || 0) >= 85 ? "bad" : (cpu.percent || 0) >= 70 ? undefined : "good"}
            sub={`${cpu.cores || 1} core · load ${(cpu.load?.[0] || 0).toFixed(2)}`}
            testid="res-kpi-cpu"
          />
          <KpiCard
            label="Memory"
            value={`${(mem.percent || 0).toFixed(1)}%`}
            icon={MemoryStick}
            tone={(mem.percent || 0) >= 85 ? "bad" : (mem.percent || 0) >= 70 ? undefined : "good"}
            sub={`${fmtBytes(mem.used)} / ${fmtBytes(mem.total)}`}
            testid="res-kpi-mem"
          />
          <KpiCard
            label="Disk (/app)"
            value={disks[0] ? `${disks[0].percent.toFixed(1)}%` : "–"}
            icon={HardDrive}
            tone={disks[0] && disks[0].percent >= 85 ? "bad" : disks[0] && disks[0].percent >= 70 ? undefined : "good"}
            sub={disks[0] ? `${fmtBytes(disks[0].used)} / ${fmtBytes(disks[0].total)}` : ""}
            testid="res-kpi-disk"
          />
          <KpiCard
            label="Backend RSS"
            value={fmtBytes(mem.backend_rss)}
            icon={Activity}
            sub={`DB data ${fmtBytes(db.data_size || 0)}`}
            testid="res-kpi-rss"
          />
        </div>

        <div className="mt-5 grid md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-medium">Utilisasi</div>
            <ProgressRow label="CPU" percent={cpu.percent || 0} right={`${(cpu.percent || 0).toFixed(1)}%`} />
            <ProgressRow label="Memory" percent={mem.percent || 0} right={`${fmtBytes(mem.used)} / ${fmtBytes(mem.total)}`} />
            {disks.map((d) => (
              <ProgressRow key={d.path} label={d.label} percent={d.percent} right={`${fmtBytes(d.used)} / ${fmtBytes(d.total)} (${d.percent.toFixed(1)}%)`} />
            ))}
          </div>
          <div className="space-y-2.5 text-[12px]">
            <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-medium">MongoDB</div>
            <div className="flex justify-between"><span className="text-muted-foreground">Data size</span><span className="num">{fmtBytes(db.data_size)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Storage size</span><span className="num">{fmtBytes(db.storage_size)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Index size</span><span className="num">{fmtBytes(db.index_size)}</span></div>
            {db.collections && Object.entries(db.collections).map(([k, v]) => (
              <div key={k} className="flex justify-between text-[11px]"><span className="text-muted-foreground">{k}</span><span className="num">{fmtNum(v || 0)}</span></div>
            ))}
          </div>
        </div>
      </SectionCard>

      {/* Historical chart */}
      <SectionCard
        eyebrow="Tren"
        title="Historis utilisasi"
        description="Sampel otomatis tiap 10 menit (disimpan 7 hari)."
        testid="res-history-card"
        actions={
          <Segmented
            value={String(hours)}
            onChange={(v) => setHours(Number(v))}
            size="sm"
            testid="res-range"
            options={[
              { value: "1", label: "1 jam" },
              { value: "6", label: "6 jam" },
              { value: "24", label: "24 jam" },
              { value: "168", label: "7 hari" },
            ]}
          />
        }
      >
        <ChartBlock points={points} hours={hours} />
      </SectionCard>

      {/* AI token usage */}
      <SectionCard
        eyebrow="Pemakaian AI"
        title={`Konsumsi token AI — ${month || "-"}`}
        description={`Total ${fmtNum(totalUsed)} token dari ${fmtNum(totalReq)} permintaan. Batas default: ${fmtNum(defaultLimit)} token / user / bulan. Admin dapat menaikkan di tab Pengguna & Kuota.`}
        testid="res-tokens-card"
      >
        {users.length === 0 ? (
          <div className="text-xs text-muted-foreground" data-testid="res-tokens-empty">Belum ada pemakaian AI bulan ini.</div>
        ) : (
          <>
            <InfoNote testid="res-tokens-note">
              <span className="inline-flex items-center gap-1.5"><Zap className="h-3.5 w-3.5 text-primary" /> Pemakaian per user bulan berjalan. Baris paling atas = konsumsi tertinggi.</span>
            </InfoNote>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-sm" data-testid="res-tokens-table">
                <thead className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
                  <tr className="border-b border-border">
                    <th className="text-left py-2 px-2 font-medium">Pengguna</th>
                    <th className="text-right py-2 px-2 font-medium">Terpakai</th>
                    <th className="text-right py-2 px-2 font-medium">Batas</th>
                    <th className="text-left py-2 px-2 font-medium w-[32%]">Progress</th>
                    <th className="text-right py-2 px-2 font-medium">Permintaan</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => {
                    const pct = u.limit > 0 ? Math.min(100, (u.used / u.limit) * 100) : 0;
                    const danger = pct >= 85;
                    return (
                      <tr key={u.email} className="border-b border-border/50 last:border-0" data-testid={`res-token-row-${u.email}`}>
                        <td className="py-2 px-2">
                          <div className="font-medium text-foreground truncate max-w-[220px]" title={u.email}>{u.email}</div>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {Object.entries(u.by_tool || {}).slice(0, 4).map(([t, v]) => (
                              <Badge key={t} variant="outline" className="text-[10px] border-border">{t}: {fmtNum(v)}</Badge>
                            ))}
                          </div>
                        </td>
                        <td className="py-2 px-2 text-right num">{fmtNum(u.used)}</td>
                        <td className="py-2 px-2 text-right num text-muted-foreground">{fmtNum(u.limit)}</td>
                        <td className="py-2 px-2">
                          <div className="h-1.5 w-full rounded-full bg-[hsl(220_10%_12%)] overflow-hidden">
                            <div className={`h-full ${danger ? "bg-[hsl(0_72%_55%)]" : pct >= 70 ? "bg-[hsl(35_80%_55%)]" : "bg-primary"} transition-all`} style={{ width: `${pct}%` }} />
                          </div>
                          <div className="text-[10px] text-muted-foreground mt-1 num">{pct.toFixed(1)}%</div>
                        </td>
                        <td className="py-2 px-2 text-right num">{fmtNum(u.requests)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </SectionCard>
    </div>
  );
}
